import { Pie, PieConfig } from '@ant-design/plots';
import { Card, Col, Row } from 'antd';
import {
  DonorStatus,
  useGetDonorAnalyticsQuery,
  useGetLocationsLazyQuery,
  useGetRegionsQuery,
} from 'gql/types';
import { useModal } from 'modules/common/hooks/useModal';
import { loadChunkedDataUntilDone } from 'modules/common/utils/loadChunkedDataUntilDone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DonationsModal from './components/DonationsModal';
import DonorTable from './components/DonorTable';
import MetricCard from './components/MetricCard';
import RegionFilter from './components/RegionFilter';
import {
  DonorStatusFilter,
  DONOR_TYPE_COLORS,
  STATUS_CHART_COLORS,
  EXPERIENCE_KEYS,
  FILTER_CONFIG,
  STATUS_KEYS,
  DONOR_TYPE_KEYS,
} from './constants';
import {
  IAnalyticsData,
  IDonorTableRow,
  IStatusBreakdownItem,
  TLocationItem,
} from './types';

const DonorDashboard: React.FC = () => {
  const { t } = useTranslation('dashboard.DonorDashboard');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedFilter, setSelectedFilter] =
    useState<DonorStatusFilter | null>(null);
  const [selectedDonor, setSelectedDonor] = useState<IDonorTableRow | null>(
    null
  );

  const {
    config: { visible: modalVisible },
    show: openModal,
    hide: closeModal,
  } = useModal();

  const [allLocations, setAllLocations] = useState<TLocationItem[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [fetchLocations] = useGetLocationsLazyQuery();

  useEffect(() => {
    let cancelled = false;
    loadChunkedDataUntilDone(100, ({ skip, limit }) =>
      fetchLocations({ variables: { input: { skip, take: limit } } }).then(
        (r) => r.data?.getLocations?.data ?? []
      )
    ).then((locations) => {
      if (!cancelled) {
        setAllLocations(locations);
        setLocationsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const getNamesByRegion = useCallback(
    (regionId: string): string[] =>
      allLocations
        .filter((loc) => loc.region?.id === regionId)
        .map((loc) => loc.name),
    [allLocations]
  );

  const { data: regionsData } = useGetRegionsQuery();

  const locationFilter = useMemo(() => {
    if (selectedRegion === 'all') return undefined;
    const names = getNamesByRegion(selectedRegion);
    return names.length ? names : undefined;
  }, [selectedRegion, getNamesByRegion]);

  const {
    data: analyticsData,
    loading: analyticsLoading,
    error: analyticsError,
  } = useGetDonorAnalyticsQuery({
    variables: {
      input: {
        filter: locationFilter ? { locations: locationFilter } : undefined,
      },
    },
    skip: selectedRegion !== 'all' && locationsLoading,
  });

  const currentData = useMemo((): IAnalyticsData | null => {
    if (!analyticsData?.getDonorAnalytics) return null;

    const analytics = analyticsData.getDonorAnalytics;
    return {
      totalActiveDonors:
        analytics.statusBreakdown.find((s) => s.status === DonorStatus.Active)
          ?.count ?? 0,
      totalSingleDonors: analytics.totalSingleDonors,
      totalDoubleDonors: analytics.totalDoubleDonors,
      totalNewDonors: analytics.totalNewDonors,
      totalExperiencedDonors: analytics.totalExperiencedDonors,
      statusBreakdown: analytics.statusBreakdown.map(
        (s): IStatusBreakdownItem => ({ status: s.status, count: s.count })
      ),
    };
  }, [analyticsData]);

  const donorTypeChartData = useMemo(() => {
    if (!currentData) return [];
    return [
      { type: t('singleDonors'), value: currentData.totalSingleDonors },
      { type: t('doubleDonors'), value: currentData.totalDoubleDonors },
    ];
  }, [currentData]);

  const statusChartData = useMemo(() => {
    if (!currentData) return [];
    return currentData.statusBreakdown.map((item) => ({
      type: item.status,
      value: item.count,
    }));
  }, [currentData]);

  const error = analyticsError?.message ?? null;
  const loading = locationsLoading || analyticsLoading;

  const handleRegionChange = useCallback((value: string) => {
    setSelectedRegion(value);
    setSelectedFilter(null);
  }, []);

  const handleDonorClick = useCallback(
    (donor: IDonorTableRow) => {
      setSelectedDonor(donor);
      openModal();
    },
    [openModal]
  );

  const donorTypePieConfig: PieConfig = {
    data: donorTypeChartData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    label: {
      text: (item: any) => `${item.type}: ${item.value}`,
      position: 'outside',
    },
    interactions: [{ type: 'element-active' }],
    color: DONOR_TYPE_COLORS,
    legend: { position: 'bottom' as const },
  };

  const statusPieConfig: PieConfig = {
    data: statusChartData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    label: {
      text: (item: any) => {
        const total = statusChartData.reduce((sum, d) => sum + d.value, 0);
        const percentage = total > 0 ? (item.value / total) * 100 : 0;
        return percentage >= 3
          ? `${item.type}: ${item.value}`
          : `${item.value}`;
      },
      position: 'outside',
      style: { fontSize: 12, fontWeight: 'bold' },
    },
    interactions: [{ type: 'element-active' }],
    color: STATUS_CHART_COLORS,
    legend: { position: 'bottom' as const },
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>{t('loading')}</div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#ff4d4f' }}>
        {t('errorLoading', { message: error })}
      </div>
    );
  }

  if (!currentData) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div
          style={{ fontSize: '18px', color: '#8c8c8c', marginBottom: '16px' }}
        >
          {t('noDataAvailable')}
        </div>
        <div style={{ color: '#bfbfbf' }}>{t('noDataDescription')}</div>
      </div>
    );
  }

  const getStatusCount = (filter: DonorStatusFilter) => {
    const donorStatuses = FILTER_CONFIG[filter].donorStatuses;
    return currentData.statusBreakdown
      .filter((item) => donorStatuses.includes(item.status as any))
      .reduce((sum, item) => sum + item.count, 0);
  };

  const donorTableTitle = selectedFilter
    ? t(FILTER_CONFIG[selectedFilter].translationKey)
    : t('recentDonors');

  return (
    <div style={{ padding: '24px' }}>
      <Row style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <RegionFilter
            value={selectedRegion}
            onChange={handleRegionChange}
            regionsData={regionsData}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={16}>
          <Card title={t('donorTypesDistribution')}>
            <Pie {...donorTypePieConfig} height={360} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Row gutter={[0, 16]}>
            {DONOR_TYPE_KEYS.map((filter) => {
              const config = FILTER_CONFIG[filter];
              return (
                <Col key={filter} span={24}>
                  <MetricCard
                    title={t(config.translationKey)}
                    icon={<config.icon />}
                    value={
                      filter === DonorStatusFilter.ActiveDonors
                        ? currentData.totalActiveDonors
                        : filter === DonorStatusFilter.SingleDonors
                        ? currentData.totalSingleDonors
                        : currentData.totalDoubleDonors
                    }
                    color={config.color}
                    filter={filter}
                    selectedFilter={selectedFilter}
                    onStatusClick={setSelectedFilter}
                  />
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        {EXPERIENCE_KEYS.map((filter) => {
          const config = FILTER_CONFIG[filter];
          return (
            <Col key={filter} xs={24} md={12}>
              <MetricCard
                title={t(config.translationKey)}
                icon={<config.icon />}
                value={
                  filter === DonorStatusFilter.NewDonors
                    ? currentData.totalNewDonors
                    : currentData.totalExperiencedDonors
                }
                color={config.color}
                filter={filter}
                selectedFilter={selectedFilter}
                onStatusClick={setSelectedFilter}
              />
            </Col>
          );
        })}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} md={16}>
          <Card title={t('statusBreakdown')}>
            <Pie {...statusPieConfig} height={400} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Row gutter={[8, 12]}>
            {STATUS_KEYS.map((filter) => {
              const config = FILTER_CONFIG[filter];
              return (
                <Col key={filter} span={12}>
                  <MetricCard
                    title={t(config.translationKey)}
                    icon={<config.icon />}
                    value={getStatusCount(filter)}
                    color={config.color}
                    filter={filter}
                    selectedFilter={selectedFilter}
                    onStatusClick={setSelectedFilter}
                  />
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Card title={donorTableTitle}>
            {!selectedFilter ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 0',
                  color: '#8c8c8c',
                  fontSize: '16px',
                }}
              >
                {t('clickToViewDonors')}
              </div>
            ) : (
              <DonorTable
                selectedFilter={selectedFilter}
                locationFilter={locationFilter}
                onDonorClick={handleDonorClick}
              />
            )}
          </Card>
        </Col>
      </Row>

      <DonationsModal
        visible={modalVisible ?? false}
        donor={selectedDonor}
        allLocations={allLocations}
        onClose={closeModal}
      />
    </div>
  );
};

export default DonorDashboard;
